/// <reference lib="dom"/>
import { hh } from "@chi/chi";
import type { ChiNode } from "@chi/chi/hooks";
import { type ParamDict, type ParamDictProps, ParamDictToProps } from "@utils/params";
import { WbaxRenderOffline } from "@chi/wbax";

export interface SynthDef<P extends ParamDict = ParamDict> {
  label: string;
  params: P;
  Component: (props: Partial<ParamDictProps<P>> & SynthVoiceProps) => ChiNode;
  duration: (opts: ParamDictProps<P>) => number;
}

export interface SynthVoiceProps {
  velocity?: number;
  onEnded?: () => void;
}

export function SynthOpts<P extends ParamDict>(
  def: { params: P },
  opts: Partial<ParamDictProps<P>> = {},
): ParamDictProps<P> {
  return { ...ParamDictToProps(def.params), ...opts };
}

export function SynthPreset<P extends ParamDict>(
  def: SynthDef<P>,
  label: string,
  values: Partial<ParamDictProps<P>>,
): SynthDef<P> {
  const params = { ...def.params };
  for (const [key, value] of Object.entries(values)) {
    (params as ParamDict)[key] = { ...params[key], value };
  }
  return {
    ...def,
    label,
    params,
    Component: (props) => hh(def.Component, { ...ParamDictToProps(params), ...props }),
  };
}

export interface RenderSynthOptions {
  sampleRate?: number;
  channels?: number;
  tail?: number;
}

export function RenderSynth<P extends ParamDict>(
  def: SynthDef<P>,
  opts: Partial<ParamDictProps<P>> = {},
  options: RenderSynthOptions = {},
): Promise<AudioBuffer> {
  const full = SynthOpts(def, opts);
  return WbaxRenderOffline(hh(def.Component, full), {
    duration: def.duration(full) + (options.tail ?? 0),
    sampleRate: options.sampleRate,
    channels: options.channels,
  });
}

export const NonZero = (v: number) => Math.max(v, 0.00001);

export function ParseNumbers(text: string): number[] {
  return text.split(/[\s,]+/).filter(Boolean).map(Number).filter(Number.isFinite);
}
